import { fetchFromPaymentsAPI, PAYMENTS_BASE_URL } from './common.js';

// ---------------------------------------------------------------------------
// publicFetch: a thin direct-fetch helper used by the public-invoice gate +
// saved-card endpoints.
//
// Why not use fetchFromPublicAPI / fetchFromPaymentsAPI?
//   - fetchFromPublicAPI throws on any non-2xx, swallowing the structured
//     error body. The DOB gate needs to read `lockedUntil` and
//     `attemptsRemaining` out of 403/429 responses to render correctly.
//   - fetchFromPaymentsAPI auto-attaches the Supabase JWT, which is wrong:
//     gate-protected endpoints accept a separate short-lived gate token,
//     not the staff JWT.
//
// Returns `{ ok, status, data }` for every response (or `{ ok:false, status:0,
// data:{ error } }` on network failure) so callers can branch on status.
// ---------------------------------------------------------------------------
async function publicFetch(endpoint, { method = 'GET', body, gateToken } = {}) {
    const headers = { 'Content-Type': 'application/json' };
    if (gateToken) headers.Authorization = `Bearer ${gateToken}`;
    try {
        const resp = await fetch(`${PAYMENTS_BASE_URL}${endpoint}`, {
            method,
            headers,
            body: body !== undefined ? JSON.stringify(body) : undefined,
        });
        let data = null;
        try { data = await resp.json(); } catch (_) { data = null; }
        return { ok: resp.ok, status: resp.status, data: data || {} };
    } catch (err) {
        console.error(`publicFetch error (${method} ${endpoint}):`, err);
        return { ok: false, status: 0, data: { error: 'Network error' } };
    }
}

// Test payments API connection
export async function testPaymentsAPI() {
    try {
        const endpoint = '/api/payments/test';
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error testing payments API:', error);
        throw error;
    }
}

// Get merchant information for a practice
export async function getMerchantInfo(practiceId) {
    try {
        const endpoint = `/api/payments/merchant?practiceId=${practiceId}`;
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching merchant info:', error);
        throw error;
    }
}

// Fetch the live Rainforest underwriting_reviews for a practice. Only
// meaningful when the merchant_application_status is NEEDS_INFORMATION;
// returns an empty object otherwise. The shape of underwriting_reviews
// mirrors the Rainforest GET /merchants/{id}/applications/{id} payload.
export async function getMerchantUnderwritingReviews(practiceId) {
    try {
        const endpoint = `/api/payments/practices/${practiceId}/merchant/underwriting-reviews`;
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching merchant underwriting reviews:', error);
        throw error;
    }
}

// Create a new merchant application for a practice
export async function createMerchantApplication(practiceId) {
    try {
        const endpoint = `/api/payments/merchant`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ practiceId: practiceId }),
        });
        return data;
    } catch (error) {
        console.error('Error creating merchant application:', error);
        throw error;
    }
}

// Get the latest Kolla PMS sync status for a practice. Powers the
// "Last synced X ago" indicator + Sync now button in the dashboard
// sidebar. Returns ``{configured: false}`` for non-Kolla practices so
// the UI can hide the indicator entirely.
export async function getKollaSyncStatus(practiceId) {
    try {
        const endpoint = `/api/payments/practices/${practiceId}/kolla-sync-status`;
        return await fetchFromPaymentsAPI(endpoint);
    } catch (error) {
        console.error('Error fetching Kolla sync status:', error);
        throw error;
    }
}

// Manually queue a Kolla sync task on the payments Celery broker.
// Fire-and-forget: returns the task id, but the actual sync runs
// asynchronously on the worker. Poll getKollaSyncStatus to detect
// completion (``lastAttemptedAt`` will move forward).
export async function triggerKollaSync(practiceId, { full = false, skipCustomTypes = false } = {}) {
    try {
        const endpoint = `/api/payments/practices/${practiceId}/kolla-sync-now`;
        return await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ full, skipCustomTypes }),
        });
    } catch (error) {
        console.error('Error triggering Kolla sync:', error);
        throw error;
    }
}

// Update merchant information for a practice
export async function updateMerchantInfo(practiceId, merchantData) {
    try {
        const endpoint = `/api/payments/merchant`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ 
                practiceId: practiceId,
                merchant: merchantData 
            }),
        });
        return data;
    } catch (error) {
        console.error('Error updating merchant info:', error);
        throw error;
    }
}

// Create a Rainforest session for merchant onboarding
export async function createMerchantSession(practiceId) {
    try {
        const endpoint = `/api/payments/merchant/session`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ practiceId: practiceId }),
        });
        return data;
    } catch (error) {
        console.error('Error creating merchant session:', error);
        throw error;
    }
}

// Create a Rainforest session for payment report component
export async function createPaymentReportSession(practiceId) {
    try {
        const endpoint = `/api/payments/payment-report/session`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ practiceId: practiceId }),
        });
        return data;
    } catch (error) {
        console.error('Error creating payment report session:', error);
        throw error;
    }
}

// Create a Rainforest session for deposit report component
export async function createDepositReportSession(practiceId) {
    try {
        const endpoint = `/api/payments/deposit-report/session`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ practiceId: practiceId }),
        });
        return data;
    } catch (error) {
        console.error('Error creating deposit report session:', error);
        throw error;
    }
}

// Create a Rainforest session for refund details component
export async function createRefundDetailsSession(practiceId) {
    try {
        const endpoint = `/api/payments/refund-details/session`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ practiceId: practiceId }),
        });
        return data;
    } catch (error) {
        console.error('Error creating refund details session:', error);
        throw error;
    }
}

// Create a Rainforest session for chargeback details component
export async function createChargebackDetailsSession(practiceId) {
    try {
        const endpoint = `/api/payments/chargeback-details/session`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ practiceId: practiceId }),
        });
        return data;
    } catch (error) {
        console.error('Error creating chargeback details session:', error);
        throw error;
    }
}

// =====================
// DEVICE MANAGEMENT
// =====================

// List registered devices for a practice
export async function listDevices(practiceId, refresh = false) {
    try {
        const endpoint = `/api/payments/devices?practiceId=${practiceId}${refresh ? '&refresh=true' : ''}`;
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error listing devices:', error);
        throw error;
    }
}

// Register a new device
export async function createDevice(practiceId, registrationCode, deviceName) {
    try {
        const endpoint = `/api/payments/devices`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                practiceId: practiceId,
                registration_code: registrationCode,
                device_name: deviceName,
            }),
        });
        return data;
    } catch (error) {
        console.error('Error creating device:', error);
        throw error;
    }
}

// Delete a device registration
export async function deleteDevice(practiceId, deviceRegistrationId) {
    try {
        const endpoint = `/api/payments/devices/${deviceRegistrationId}?practiceId=${practiceId}`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'DELETE',
        });
        return data;
    } catch (error) {
        console.error('Error deleting device:', error);
        throw error;
    }
}

// Send invoice payment to a device.
//
// `declaredCardType` is the staff's pre-tap classification ('CREDIT' or
// 'DEBIT') — the device API has no BIN-lookup, so when the practice has
// surcharging or debit-billing-override enabled the staff must declare
// the card type before tap so the backend can mint a replacement
// payin_config carrying the surcharge / override. Pass null/undefined
// to send the original config unchanged (legacy behaviour).
//
// `saveCardOnPay` is the staff-driven analogue of the public invoice
// page's "Save my card for next time" checkbox. ``true``/``false``
// writes ``invoice.saveOnPayPreference`` before the device push;
// ``null``/undefined leaves it untouched (the webhook treats unset as
// opt-in, matching the public page's pre-checked default).
export async function sendInvoiceToDevice(
    practiceId,
    invoiceId,
    deviceRegistrationId,
    declaredCardType = null,
    saveCardOnPay = null
) {
    try {
        const endpoint = `/api/payments/invoices/${invoiceId}/send-to-device`;
        const body = {
            practiceId: practiceId,
            device_registration_id: deviceRegistrationId,
        };
        if (declaredCardType) {
            body.declared_card_type = String(declaredCardType).toUpperCase();
        }
        if (saveCardOnPay !== null && saveCardOnPay !== undefined) {
            body.save_card_on_pay = !!saveCardOnPay;
        }
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify(body),
        });
        return data;
    } catch (error) {
        console.error('Error sending invoice to device:', error);
        throw error;
    }
}

// Tokenize-only flow on a physical terminal: mint a payment_method_config
// scoped to (practice, patient) via the invoice context, then present it
// on the device for the patient to tap and save (no charge). The card
// lands in the vault automatically via the payment_method.activated
// webhook. Used by the staff "Add Card" button in the send-to-device
// modal.
export async function saveCardOnDevice(practiceId, invoiceId, deviceRegistrationId) {
    try {
        const endpoint = `/api/payments/invoices/${invoiceId}/save-card-on-device`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                practiceId: practiceId,
                device_registration_id: deviceRegistrationId,
            }),
        });
        return data;
    } catch (error) {
        console.error('Error saving card on device:', error);
        throw error;
    }
}

// Set a device as the default device
export async function setDefaultDevice(practiceId, deviceRegistrationId) {
    try {
        const endpoint = `/api/payments/devices/${deviceRegistrationId}/set-default?practiceId=${practiceId}`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
        });
        return data;
    } catch (error) {
        console.error('Error setting default device:', error);
        throw error;
    }
}

// Cancel device presentation
export async function cancelDevicePresentation(practiceId, deviceRegistrationId) {
    try {
        const endpoint = `/api/payments/devices/${deviceRegistrationId}/cancel?practiceId=${practiceId}`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
        });
        return data;
    } catch (error) {
        console.error('Error cancelling device presentation:', error);
        throw error;
    }
}

// Curated subset of Rainforest's sandbox test cards covering the credit /
// debit / decline paths we actually want to QA. Full enum lives at
// https://docs.rainforestpay.com/reference/simulate_on_device
// Shared by the Device Management tab and the staff "Waiting for Payment"
// modal so both QA surfaces stay in sync.
export const SIMULATE_DEVICE_PRESETS = [
    { id: 'visa-credit', label: 'Visa — Credit (success)', event: 'PRESENTED', card: 'VISA_SUCCESS' },
    { id: 'visa-debit', label: 'Visa — Debit (success)', event: 'PRESENTED', card: 'VISA_DEBIT_SUCCESS' },
    { id: 'mc-credit', label: 'Mastercard — Credit (success)', event: 'PRESENTED', card: 'MC_SUCCESS' },
    { id: 'mc-debit', label: 'Mastercard — Debit (success)', event: 'PRESENTED', card: 'MC_DEBIT_SUCCESS' },
    { id: 'mc-prepaid', label: 'Mastercard — Prepaid (success)', event: 'PRESENTED', card: 'MC_PREPAID_SUCCESS' },
    { id: 'amex-credit', label: 'Amex — Credit (success)', event: 'PRESENTED', card: 'AMEX_SUCCESS' },
    { id: 'amex-debit', label: 'Amex — Debit (success)', event: 'PRESENTED', card: 'AMEX_DEBIT_SUCCESS' },
    { id: 'discover-credit', label: 'Discover — Credit (success)', event: 'PRESENTED', card: 'DISCOVER_SUCCESS' },
    { id: 'decline-generic', label: 'Decline — generic', event: 'PRESENTED', card: 'GENERIC_DECLINE' },
    { id: 'decline-funds', label: 'Decline — insufficient funds', event: 'PRESENTED', card: 'INSUFFICIENT_FUNDS' },
    { id: 'cancel', label: 'User pressed cancel', event: 'CANCEL', card: null },
];

// Heuristic: every Rainforest sandbox ID is prefixed `sbx_` (e.g.
// `sbx_devreg_…`, `sbx_mid_…`). Production IDs never carry that prefix.
// Lets us gate sandbox-only UI affordances (like Simulate Tap) without
// having to plumb the practice's `merchant.demo` flag everywhere.
export function isSandboxRainforestId(rainforestId) {
    return typeof rainforestId === 'string' && rainforestId.startsWith('sbx_');
}

// Sandbox-only: simulate a tap/swipe/cancel on a virtual Rainforest device.
// `event` is 'PRESENTED' or 'CANCEL'; `card` is required when event='PRESENTED'
// and must be one of Rainforest's documented sandbox test cards (e.g.
// 'VISA_DEBIT_SUCCESS', 'MC_SUCCESS', 'GENERIC_DECLINE').
//
// NB: this endpoint commonly returns 4xx (no payin currently presented,
// stale id, etc.) and the QA experience hinges on staff seeing the
// actual reason. The shared `fetchFromPaymentsAPI` wrapper throws a
// generic "API error: 400" on non-2xx responses without reading the
// JSON body, which would surface a useless toast. We therefore call
// fetch directly here so we can pluck `data.error` off the body and
// return it in the same `{success, error}` shape the rest of the app
// expects, instead of throwing.
export async function simulateDeviceInteraction(practiceId, deviceRegistrationId, event, card = null) {
    try {
        const endpoint = `/api/payments/devices/${deviceRegistrationId}/simulate?practiceId=${practiceId}`;
        const body = card ? { event, card } : { event };

        // `doFetchWithRetry` caches the Supabase access token in
        // localStorage on every authenticated request. Read it directly
        // instead of re-importing the wrapper so we keep this call
        // synchronous-ish and don't need to plumb a separate auth
        // helper through. If the token is somehow missing the request
        // will 401 and we'll surface that as the error message — fine
        // for sandbox QA.
        const jwt = localStorage.getItem('jwtToken');
        const headers = { 'Content-Type': 'application/json' };
        if (jwt) headers.Authorization = `Bearer ${jwt}`;

        const response = await fetch(`${PAYMENTS_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers,
            body: JSON.stringify(body),
        });

        let data = null;
        try { data = await response.json(); } catch (_) { /* non-JSON body */ }

        if (!response.ok) {
            const error = (data && data.error) || `${response.status} ${response.statusText}`;
            return { success: false, error };
        }
        return data || { success: true };
    } catch (error) {
        console.error('Error simulating device interaction:', error);
        return { success: false, error: error?.message || 'Network error' };
    }
}

// Send invoice email
export async function sendInvoiceEmail(practiceId, invoiceId, email) {
    try {
        const endpoint = `/api/payments/invoices/${invoiceId}/send-email`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                practiceId: practiceId,
                email: email,
            }),
        });
        return data;
    } catch (error) {
        console.error('Error sending invoice email:', error);
        throw error;
    }
}

// Send an invoice payment link via SMS. Mirrors sendInvoiceEmail — the
// InvoiceTab flow calls the same /send-sms route inline; this wrapper lets
// other surfaces (e.g. LedgerTab statements) reuse it.
export async function sendInvoiceSms(practiceId, invoiceId, phoneNumber) {
    try {
        const endpoint = `/api/payments/invoices/${invoiceId}/send-sms`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                practiceId: practiceId,
                phoneNumber: phoneNumber,
            }),
        });
        return data;
    } catch (error) {
        console.error('Error sending invoice SMS:', error);
        throw error;
    }
}

// Email a PDF receipt for an already-settled invoice. Distinct from
// sendInvoiceEmail, which sends a *payment request* with a pay link — this
// confirms money already received and attaches the receipt as a PDF. The
// backend refuses invoices that haven't settled.
export async function sendInvoiceReceipt(practiceId, invoiceId, email) {
    try {
        const endpoint = `/api/payments/invoices/${invoiceId}/send-receipt`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                practiceId: practiceId,
                email: email,
            }),
        });
        return data;
    } catch (error) {
        console.error('Error sending invoice receipt:', error);
        throw error;
    }
}

export async function logInvoiceActivity(practiceId, invoiceId, action, details = {}) {
    try {
        const endpoint = `/api/payments/invoices/${invoiceId}/log-activity`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                practiceId: practiceId,
                action: action,
                details: details,
            }),
        });
        return data;
    } catch (error) {
        console.error('Error logging invoice activity:', error);
        throw error;
    }
}

// =====================
// INVOICE MANAGEMENT
// =====================

// Create a new patient invoice.
//
// `options.supersede` (default false) opts in to replacing any existing
// pending invoice for the same patient. When false (the default), the
// backend returns `{success: false, conflict: true, existing_invoice}`
// instead of creating a duplicate — the caller is expected to confirm
// with the user and retry with `supersede: true`.
export async function createInvoice(practiceId, invoiceData, options = {}) {
    try {
        const endpoint = `/api/payments/invoices`;
        const { supersede = false } = options;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                practiceId: practiceId,
                supersede,
                ...invoiceData,
            }),
        });
        return data;
    } catch (error) {
        console.error('Error creating invoice:', error);
        throw error;
    }
}

// Manually cancel (void) a pending invoice. Flips the invoice to
// status='cancelled', stamps cancelledAt/cancelledBy, and appends an
// 'invoice_cancelled' activity log entry. Idempotent. The server
// rejects cancelling paid / processing / superseded invoices — for
// those, staff must use the refund flow or the supersede chain.
export async function cancelInvoice(practiceId, invoiceId, reason = null) {
    try {
        const endpoint = `/api/payments/invoices/${encodeURIComponent(invoiceId)}/cancel`;
        const body = { practiceId };
        if (reason) body.reason = reason;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return data;
    } catch (error) {
        console.error('Error cancelling invoice:', error);
        throw error;
    }
}

// Mark a "Needs attention" invoice as handled so it drops off the list.
// Stamps attentionResolved (who/when) and an 'attention_resolved' activity
// entry; status and PMS reconciliation are left untouched. Idempotent.
export async function resolveInvoiceAttention(practiceId, invoiceId, note = null) {
    try {
        const endpoint = `/api/payments/invoices/${encodeURIComponent(invoiceId)}/resolve-attention`;
        const body = { practiceId };
        if (note) body.note = note;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return data;
    } catch (error) {
        console.error('Error resolving invoice attention:', error);
        throw error;
    }
}

// Resolve several Needs attention invoices in one request (max 200). Returns
// { success, counts: {resolved, already, not_found, conflict, error},
// results: [{id, outcome}], invoices: [...saved docs] }.
export async function resolveInvoiceAttentionBulk(practiceId, invoiceIds, note = null) {
    try {
        const body = { practiceId, invoiceIds };
        if (note) body.note = note;
        const data = await fetchFromPaymentsAPI('/api/payments/invoices/resolve-attention/bulk', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return data;
    } catch (error) {
        console.error('Error bulk resolving invoice attention:', error);
        throw error;
    }
}

// =====================
// TRANSACTIONS - Daily PMS Transactions
// =====================

// =====================
// CLAIMS - Insurance Claims via Kolla
// =====================

export async function getClaims(practiceId, options = {}) {
    try {
        const { state, startDate, endDate, daysBack } = options;

        let endpoint = `/api/payments/claims/${practiceId}`;
        const params = new URLSearchParams();

        if (startDate && endDate) {
            params.append('start_date', startDate);
            params.append('end_date', endDate);
        } else if (daysBack !== undefined && daysBack !== null) {
            params.append('days_back', String(daysBack));
        }

        if (state) {
            params.append('state', state);
        }

        if (params.toString()) {
            endpoint += `?${params.toString()}`;
        }

        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching claims:', error);
        throw error;
    }
}

// A patient's claims an insurance invoice can be linked to, newest first.
export async function getPatientPayableClaims(practiceId, patientId) {
    return fetchFromPaymentsAPI(
        `/api/payments/practices/${practiceId}/patients/${encodeURIComponent(patientId)}/claims`
    );
}

// =====================
// LEDGER - Per-patient combined view (Cosmos: KollaContacts + KollaTransactions + KollaClaims)
// =====================

export async function searchLedgerPatients(practiceId, query, options = {}) {
    try {
        const { limit = 20 } = options;
        const params = new URLSearchParams();
        if (query) params.append('q', query);
        if (limit) params.append('limit', String(limit));

        let endpoint = `/api/payments/ledger/${practiceId}/patients`;
        if (params.toString()) endpoint += `?${params.toString()}`;

        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error searching ledger patients:', error);
        throw error;
    }
}

export async function getLatestSettled(practiceId, options = {}) {
    try {
        const { date } = options;
        let endpoint = `/api/payments/ledger/${practiceId}/latest-settled`;
        if (date) {
            const params = new URLSearchParams({ date });
            endpoint += `?${params.toString()}`;
        }
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching latest-settled:', error);
        throw error;
    }
}

// AR Aging board snapshot (0-30/31-60/61-90/90+, patient vs insurance AR).
// Reads the nightly-precomputed snapshot; pass { refresh: true } to force a
// fresh recompute server-side. Returns the full API envelope so the caller
// can read `enabled` / `computedOnRequest` alongside `snapshot`.
export async function getArAging(practiceId, options = {}) {
    try {
        const { refresh = false } = options;
        let endpoint = `/api/payments/ledger/${practiceId}/ar-aging`;
        if (refresh) endpoint += '?refresh=true';
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching AR aging:', error);
        throw error;
    }
}

// Schedule tab: the OpenDental appointment book for [start, end] (inclusive,
// YYYY-MM-DD, practice-local), each visit tagged with its post-visit
// collection state. See payments/backend/visit_schedule.py.
export async function getVisitSchedule(practiceId, { start, end } = {}) {
    const params = new URLSearchParams();
    if (start) params.set('start', start);
    if (end) params.set('end', end);
    return fetchFromPaymentsAPI(
        `/api/payments/ledger/${encodeURIComponent(practiceId)}/visit-schedule?${params.toString()}`
    );
}

// General Reports (DI-style daily / weekly / monthly report). `date` is a
// YYYY-MM-DD anchor in the practice timezone; omit it for today.
export async function getGeneralReport(practiceId, { period = 'daily', date = null, refresh = false } = {}) {
    const params = new URLSearchParams({ period });
    if (date) params.set('date', date);
    if (refresh) params.set('refresh', 'true');
    return fetchFromPaymentsAPI(
        `/api/payments/reports/${encodeURIComponent(practiceId)}/general?${params.toString()}`
    );
}

export async function getGeneralReportGoals(practiceId) {
    return fetchFromPaymentsAPI(`/api/payments/reports/${encodeURIComponent(practiceId)}/general/goals`);
}

// Replaces the practice's goals: { daily|weekly|monthly: { goalKey: number } }.
export async function saveGeneralReportGoals(practiceId, goals) {
    return fetchFromPaymentsAPI(`/api/payments/reports/${encodeURIComponent(practiceId)}/general/goals`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ goals }),
    });
}

// Fetch every invoice for a practice (full docs, newest first). Used by the
// AR Aging tab to overlay a live invoice indicator + timeline onto each
// household row. Kept live (not baked into the nightly snapshot) so a
// just-created / just-paid invoice reflects immediately.
export async function getInvoices(practiceId) {
    try {
        const data = await fetchFromPaymentsAPI(
            `/api/payments/invoices?practiceId=${encodeURIComponent(practiceId)}`
        );
        return data;
    } catch (error) {
        console.error('Error fetching invoices:', error);
        throw error;
    }
}

export async function getPatientLedger(practiceId, contactId) {
    try {
        const endpoint = `/api/payments/ledger/${practiceId}/patient/${encodeURIComponent(contactId)}`;
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching patient ledger:', error);
        throw error;
    }
}

export async function getHouseholdLedger(practiceId, contactId) {
    try {
        const endpoint = `/api/payments/ledger/${practiceId}/household/${encodeURIComponent(contactId)}`;
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching household ledger:', error);
        throw error;
    }
}

// Live-read this patient's transactions + claims from the PMS (Kolla) and
// persist them to our cache. The ledger GET endpoints read DB-only, so the
// caller should re-fetch the ledger after this resolves to show fresh data.
// Syncs the whole household when the patient belongs to one.
export async function syncPatientLedgerFromPMS(practiceId, contactId) {
    try {
        const endpoint = `/api/payments/ledger/${practiceId}/patient/${encodeURIComponent(contactId)}/sync`;
        // Send a JSON body (with practiceId) so the backend's
        // get_practice_ids_from_request can parse it cleanly. A
        // Content-Type: application/json header with an EMPTY body makes
        // Flask's req.get_json() raise a 400 before our handler runs.
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ practiceId }),
        });
        return data;
    } catch (error) {
        console.error('Error syncing patient ledger from PMS:', error);
        throw error;
    }
}

// Ledger tab bulk "Invoice & send": one statement invoice per household for
// the selected settled-claims rows, texted and/or emailed. ``dryRun: true``
// powers the confirmation step (evaluates every skip rule, writes nothing).
export async function bulkInvoiceFromLedger(practiceId, { contactIds, channels, dryRun }) {
    try {
        const endpoint = `/api/payments/ledger/${practiceId}/bulk-invoice`;
        return await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ practiceId, contactIds, channels, dryRun }),
        });
    } catch (error) {
        console.error('Error running bulk invoice:', error);
        throw error;
    }
}

// Re-credit a Newton-posted ledger payment to a different provider (drives
// the provider dropdown on the green rows of the patient statement). The
// backend writes the change to the PMS via Zatanna and re-syncs our cache.
export async function updateLedgerPaymentProvider(practiceId, remoteId, providerId) {
    try {
        const endpoint = `/api/payments/ledger/${practiceId}/payment/${encodeURIComponent(remoteId)}/provider`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ practiceId, providerId }),
        });
        return data;
    } catch (error) {
        console.error('Error updating ledger payment provider:', error);
        throw error;
    }
}

// Break down a Newton-posted payment into several per-provider rows (drives
// the "Break down" modal on the green rows of the patient statement). The
// backend deletes the original PMS payment and creates one per split line.
// `splits` = [{ providerId, amount }] where amount is a positive decimal
// string that must sum to the original payment amount.
export async function splitLedgerPayment(practiceId, remoteId, splits, options = {}) {
    try {
        const endpoint = `/api/payments/ledger/${practiceId}/payment/${encodeURIComponent(remoteId)}/split`;
        const body = { practiceId, splits };
        if (options.allowUnbalanced) body.allowUnbalanced = true;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(body),
        });
        return data;
    } catch (error) {
        console.error('Error splitting ledger payment:', error);
        throw error;
    }
}

// Delete (or void) a Newton-posted ledger payment from the PMS (drives the
// "Delete row" action on the green rows of the patient statement). The backend
// removes the PMS payment via Zatanna and drops it from our cache.
export async function deleteLedgerPayment(practiceId, remoteId, reason) {
    try {
        const endpoint = `/api/payments/ledger/${practiceId}/payment/${encodeURIComponent(remoteId)}/delete`;
        const data = await fetchFromPaymentsAPI(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ practiceId, reason }),
        });
        return data;
    } catch (error) {
        console.error('Error deleting ledger payment:', error);
        throw error;
    }
}

export async function getTransactions(practiceId, options = {}) {
    try {
        const { date, startDate, endDate, daysBack = 1 } = options;
        
        let endpoint = `/api/payments/transactions/${practiceId}`;
        const params = new URLSearchParams();
        
        if (startDate && endDate) {
            // Date range mode
            params.append('start_date', startDate);
            params.append('end_date', endDate);
        } else if (date) {
            // Single date mode (legacy)
            params.append('date', date);
        } else {
            // Days back mode
            params.append('days_back', daysBack.toString());
        }
        
        if (params.toString()) {
            endpoint += `?${params.toString()}`;
        }
        
        const data = await fetchFromPaymentsAPI(endpoint);
        return data;
    } catch (error) {
        console.error('Error fetching transactions:', error);
        throw error;
    }
}

// =====================
// PUBLIC INVOICE — DOB GATE + SAVED PAYMENT METHODS
// =====================
//
// All of these go through `publicFetch` (defined at the top of this file)
// so the caller can read structured error bodies for 403/429 responses.
// They return the raw `{ok, status, data}` envelope instead of just the
// payload, because the DOB gate UX hinges on those fields.

// Submit DOB to unlock saved-card management for an invoice.
// On 200 the data carries `{gateToken, firstName, hasCards}`.
// On 403 (mismatch): `{attemptsRemaining}`. On 429: `{lockedUntil}`.
// On 412 the DOB is unavailable for the patient — the caller should
// silently fall back to the existing guest-pay flow.
export async function verifyInvoiceDob(hash, { birthDate, captchaToken }) {
    return publicFetch(`/api/payments/public/invoice/${hash}/verify-dob`, {
        method: 'POST',
        body: { birthDate, captchaToken },
    });
}

// List the patient's saved cards for a public invoice.
// Requires a valid gate token. Returns display-safe rows only (PM_<...>
// ids, never mtd_).
export async function listInvoicePaymentMethods(hash, gateToken) {
    return publicFetch(`/api/payments/public/invoice/${hash}/payment-methods`, {
        method: 'GET',
        gateToken,
    });
}

// Mint a Rainforest Payment Component session for SAVING a card on this
// invoice (no immediate charge). Returns `{session_key, payment_method_config_id}`.
export async function createInvoicePaymentMethodSession(hash, gateToken) {
    return publicFetch(
        `/api/payments/public/invoice/${hash}/payment-methods/session`,
        { method: 'POST', gateToken },
    );
}

// After the Payment Component fires `approved` with a payment_method_id,
// persist it server-side (which also re-fetches authoritative state from
// Rainforest before writing the vault doc).
export async function saveInvoicePaymentMethod(hash, gateToken, { paymentMethodId, makeDefault = false }) {
    return publicFetch(`/api/payments/public/invoice/${hash}/payment-methods`, {
        method: 'POST',
        gateToken,
        body: { payment_method_id: paymentMethodId, makeDefault },
    });
}

// Soft-delete (deactivate) a saved card from the public page.
export async function deleteInvoicePaymentMethod(hash, gateToken, vaultId) {
    return publicFetch(
        `/api/payments/public/invoice/${hash}/payment-methods/${vaultId}`,
        { method: 'DELETE', gateToken },
    );
}

// Promote a saved card to the patient's default from the public page.
// The server clears ``isDefault`` on every other card under the same
// patient in a single call, so the response carries the freshly-
// reconciled wallet (`paymentMethods`) — callers can either trust that
// list or refetch.
export async function setDefaultInvoicePaymentMethod(hash, gateToken, vaultId) {
    return publicFetch(
        `/api/payments/public/invoice/${hash}/payment-methods/${vaultId}/default`,
        { method: 'POST', gateToken },
    );
}

// Persist the "save my card after this payment" preference for an
// invoice. No gate token required — the public hash is already access
// control for the invoice itself, and this flag carries no financial
// weight (it just controls whether the card gets vaulted after a
// successful payin).
export async function setInvoiceSaveOnPay(hash, save) {
    return publicFetch(
        `/api/payments/public/invoice/${hash}/save-on-pay`,
        { method: 'POST', body: { save: !!save } },
    );
}

// Quote a credit-card surcharge live during checkout. Called from
// PublicInvoice.js whenever the Rainforest Payment Component fires a
// `bin-lookup` event (i.e. as the patient finishes typing a PAN).
//
// The server runs `compute_surcharge` against the practice's settings
// and the BIN-lookup payload. If the resulting amount differs from
// what the current payin_config will charge, it mints a replacement
// payin_config and returns its id; otherwise it echoes the existing
// id with `changed: false`.
//
// Hash-only auth — same model as save-on-pay. Final amount enforcement
// happens server-side via the payin_config Rainforest charges against,
// so a tampered request can only DECREASE the surcharge, never inflate it.
//
// Returns `{ ok, status, data: { success, surcharge, payinConfigId,
// amountCents, changed } }`.
export async function quoteSurcharge(hash, binLookup) {
    return publicFetch(
        `/api/payments/public/invoice/${hash}/quote-surcharge`,
        { method: 'POST', body: { binLookup } },
    );
}

// Synchronously vault the card the patient just paid with. Called from
// the main payin form's `approved` handler when "Save my card" is
// checked, so the saved-card list updates immediately without waiting
// for the (eventually-consistent) Rainforest payin webhook.
export async function saveCardFromPayin(hash, paymentMethodId) {
    return publicFetch(
        `/api/payments/public/invoice/${hash}/save-from-payin`,
        { method: 'POST', body: { paymentMethodId } },
    );
}

// Patient triggers a charge against one of their saved cards from the
// public invoice page. Returns 202 on submission — the actual paid
// status comes through the existing payin webhook.
export async function chargeInvoiceWithSavedCardPublic(hash, gateToken, vaultId) {
    return publicFetch(
        `/api/payments/public/invoice/${hash}/charge-saved`,
        { method: 'POST', gateToken, body: { vaultId } },
    );
}

// =====================
// PAYMENT METHODS — STAFF DASHBOARD
// =====================

// List a patient's saved payment methods from the staff dashboard. Uses
// the standard practice JWT.
//
// `options.invoiceId` (optional) attaches a per-card `surchargePreview`
// computed against that invoice's amount. The InvoiceTab "Charge a
// saved card on file" modal passes this so the radio rows can show the
// actual total including any surcharge. Omit when listing cards
// outside the context of a specific invoice (e.g. SettingsTab) — the
// server still returns rows, just without preview data.
export async function listPatientPaymentMethods(practiceId, patientId, options = {}) {
    try {
        const params = new URLSearchParams({ practiceId });
        if (options.includeDeactivated) params.append('includeDeactivated', 'true');
        if (options.invoiceId) params.append('invoiceId', options.invoiceId);
        const endpoint = `/api/payments/patients/${encodeURIComponent(patientId)}/payment-methods?${params.toString()}`;
        return await fetchFromPaymentsAPI(endpoint);
    } catch (error) {
        console.error('Error listing patient payment methods:', error);
        throw error;
    }
}

// Staff-side "Add a card manually" — mirrors the public-page
// AddPaymentMethod component but authenticated via the staff JWT
// instead of a gate token. Used by the Charge-Card-on-File modal so
// staff can vault a card on behalf of the patient (in person / over
// the phone) without sending the public link or presenting a terminal.
//
// Direct fetch (not fetchFromPaymentsAPI) because Rainforest /
// merchant-config errors come back as 4xx with structured
// ``{success:false, error}`` bodies that the standard helper
// swallows. We want the actual reason ("No merchant configured for
// this practice", etc.) to surface in the inline form instead of a
// generic "API error: 400".
//
// Returns ``{ok, status, data}`` shape. ``data.success`` is true on
// the happy path; ``data.error`` carries the message on failure.
export async function createStaffSaveCardSession(practiceId, patientId) {
    const endpoint = `/api/payments/practices/${encodeURIComponent(practiceId)}/patients/${encodeURIComponent(patientId)}/save-card-session?practiceId=${encodeURIComponent(practiceId)}`;
    const token = localStorage.getItem('jwtToken');
    let resp;
    try {
        resp = await fetch(`${PAYMENTS_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({}),
        });
    } catch (err) {
        console.error('Network error minting staff save-card session:', err);
        return { ok: false, status: 0, data: { error: 'Network error' } };
    }
    let data = null;
    try { data = await resp.json(); } catch (_) { data = null; }
    return { ok: resp.ok, status: resp.status, data: data || {} };
}

// After the Rainforest Payment Component fires ``approved`` with a
// payment_method_id, the browser POSTs that id back here. Server
// re-fetches the payment_method authoritatively, validates the
// metadata it stamped at config creation, and vault_upserts. Returns
// the new vault row so the UI can auto-select it.
//
// Direct fetch for the same reason as ``createStaffSaveCardSession``:
// metadata-mismatch / Rainforest-fetch failures need to surface their
// real error body to the inline form.
export async function staffSavePaymentMethod(practiceId, patientId, { paymentMethodId, makeDefault } = {}) {
    const endpoint = `/api/payments/practices/${encodeURIComponent(practiceId)}/patients/${encodeURIComponent(patientId)}/save-card?practiceId=${encodeURIComponent(practiceId)}`;
    const token = localStorage.getItem('jwtToken');
    let resp;
    try {
        resp = await fetch(`${PAYMENTS_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                payment_method_id: paymentMethodId,
                makeDefault: !!makeDefault,
            }),
        });
    } catch (err) {
        console.error('Network error saving staff-entered payment method:', err);
        return { ok: false, status: 0, data: { error: 'Network error' } };
    }
    let data = null;
    try { data = await resp.json(); } catch (_) { data = null; }
    return { ok: resp.ok, status: resp.status, data: data || {} };
}

// Staff-side soft-delete of a saved card. Used by the "Collect payment →
// Virtual terminal" flow to remove a card that was transiently vaulted only
// so it could be charged, when staff did NOT opt to keep it on file. Direct
// fetch so structured error bodies surface. Returns {ok, status, data}.
export async function staffDeletePaymentMethod(practiceId, patientId, vaultId) {
    const endpoint = `/api/payments/practices/${encodeURIComponent(practiceId)}/patients/${encodeURIComponent(patientId)}/payment-methods/${encodeURIComponent(vaultId)}?practiceId=${encodeURIComponent(practiceId)}`;
    const token = localStorage.getItem('jwtToken');
    let resp;
    try {
        resp = await fetch(`${PAYMENTS_BASE_URL}${endpoint}`, {
            method: 'DELETE',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
        });
    } catch (err) {
        console.error('Network error deleting staff payment method:', err);
        return { ok: false, status: 0, data: { error: 'Network error' } };
    }
    let data = null;
    try { data = await resp.json(); } catch (_) { data = null; }
    return { ok: resp.ok, status: resp.status, data: data || {} };
}

// Update practice-level payment toggles (auto-charge / require-DOB).
// Body fields are all optional — pass only the toggles you want changed.
export async function updatePracticePaymentsSettings(practiceId, settings) {
    try {
        const endpoint = `/api/payments/practices/${encodeURIComponent(practiceId)}/payments-settings?practiceId=${encodeURIComponent(practiceId)}`;
        return await fetchFromPaymentsAPI(endpoint, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(settings),
        });
    } catch (error) {
        console.error('Error updating practice payment settings:', error);
        throw error;
    }
}

// Sync the practice's PMS (Dentrix/Zatanna) payment types onto the practice
// doc so Settings can render a default-picker + the derived brand→type map.
// Returns { paymentTypes, cardPaymentTypeMap, suggestedCardPaymentTypeMap,
// seededCardPaymentTypeMap, defaultPaymentTypeId, syncedAt }.
export async function syncLedgerPaymentTypes(practiceId) {
    try {
        const endpoint = `/api/payments/practices/${encodeURIComponent(practiceId)}/ledger-payment-types/sync?practiceId=${encodeURIComponent(practiceId)}`;
        return await fetchFromPaymentsAPI(endpoint, { method: 'POST' });
    } catch (error) {
        console.error('Error syncing ledger payment types:', error);
        throw error;
    }
}

// Fetch practice info — includes timezone + the new `payments` toggles.
export async function getPracticeInfo(practiceId) {
    try {
        const endpoint = `/api/payments/practices/${encodeURIComponent(practiceId)}/info?practiceId=${encodeURIComponent(practiceId)}`;
        return await fetchFromPaymentsAPI(endpoint);
    } catch (error) {
        console.error('Error loading practice info:', error);
        throw error;
    }
}

export async function listEnabledPractices() {
    try {
        const data = await fetchFromPaymentsAPI('/api/payments/enabled-practices');
        return Array.isArray(data?.practices) ? data.practices : [];
    } catch (error) {
        console.error('Error loading enabled practices:', error);
        throw error;
    }
}

// =============================================================================
// Payment plans (staff)
// =============================================================================
// Thin wrappers over the /api/payments/payment-plans endpoints. Mirrors the
// shape of the saved-card / invoice service helpers above:
//   * GET / list-shaped calls go through fetchFromPaymentsAPI (auto-attaches
//     the staff JWT, throws on non-2xx).
//   * Mutating calls that may return a structured error body (validation,
//     409 conflicts) use a raw fetch so the UI can branch on
//     ``data.code`` without losing the error message in a generic
//     ``API error: 400`` Error throw — same convention as
//     ``chargeInvoiceWithSavedCard`` above.
// =============================================================================

async function paymentsRawPost(endpoint, body) {
    const token = localStorage.getItem('jwtToken');
    let resp;
    try {
        resp = await fetch(`${PAYMENTS_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body || {}),
        });
    } catch (err) {
        console.error(`Network error POST ${endpoint}:`, err);
        return { ok: false, status: 0, data: { error: 'Network error' } };
    }
    let data = null;
    try { data = await resp.json(); } catch (_) { data = null; }
    return { ok: resp.ok, status: resp.status, data: data || {} };
}

// Enroll a patient in a monthly payment plan. No charge fires; the cron
// picks up the plan on/after ``startDate``.
export async function createPaymentPlan(payload) {
    return paymentsRawPost('/api/payments/payment-plans', payload);
}

// List plans for a practice. ``options`` may include ``patientId`` (string)
// and ``statuses`` (array of 'active'|'paused'|'defaulted'|'completed'|'cancelled').
export async function listPaymentPlans(practiceId, options = {}) {
    try {
        const params = new URLSearchParams({ practiceId });
        if (options.patientId) params.append('patientId', options.patientId);
        if (Array.isArray(options.statuses) && options.statuses.length) {
            params.append('status', options.statuses.join(','));
        }
        const endpoint = `/api/payments/payment-plans?${params.toString()}`;
        return await fetchFromPaymentsAPI(endpoint);
    } catch (error) {
        console.error('Error listing payment plans:', error);
        throw error;
    }
}

// Fetch a single plan + its side-loaded installment invoices.
export async function getPaymentPlan(practiceId, planId) {
    try {
        const params = new URLSearchParams({ practiceId });
        const endpoint = `/api/payments/payment-plans/${encodeURIComponent(planId)}?${params.toString()}`;
        return await fetchFromPaymentsAPI(endpoint);
    } catch (error) {
        console.error('Error fetching payment plan:', error);
        throw error;
    }
}

export async function pausePaymentPlan(practiceId, planId, note) {
    return paymentsRawPost(
        `/api/payments/payment-plans/${encodeURIComponent(planId)}/pause`,
        { practiceId, note },
    );
}

export async function resumePaymentPlan(practiceId, planId, note) {
    return paymentsRawPost(
        `/api/payments/payment-plans/${encodeURIComponent(planId)}/resume`,
        { practiceId, note },
    );
}

export async function cancelPaymentPlan(practiceId, planId, reason) {
    return paymentsRawPost(
        `/api/payments/payment-plans/${encodeURIComponent(planId)}/cancel`,
        { practiceId, reason },
    );
}

// Swap the saved card a plan draws from. ``autoResume`` defaults to true so
// updating the card on a defaulted plan immediately re-activates it with
// nextChargeDate=today.
export async function updatePaymentPlanCard(practiceId, planId, paymentMethodVaultId, options = {}) {
    return paymentsRawPost(
        `/api/payments/payment-plans/${encodeURIComponent(planId)}/update-card`,
        {
            practiceId,
            paymentMethodVaultId,
            autoResume: options.autoResume !== false,
        },
    );
}

// Fire the next installment immediately. Server claims the plan + attempts
// the charge; the webhook will eventually advance / fail the plan as usual.
export async function chargePaymentPlanNow(practiceId, planId) {
    return paymentsRawPost(
        `/api/payments/payment-plans/${encodeURIComponent(planId)}/charge-now`,
        { practiceId },
    );
}

// Staff-initiated 'charge card on file' for an invoice. Returns 202 on
// submission; status will flip to processing/paid via the payin webhook.
//
// Uses a raw fetch (not fetchFromPaymentsAPI) because Rainforest refusal
// reasons come back as 4xx with structured `{error, code, details}`
// bodies that the standard helper would swallow into an error message.
// We still inject the staff JWT manually.
export async function chargeInvoiceWithSavedCard(practiceId, invoiceId, vaultId) {
    const endpoint = `/api/payments/invoices/${encodeURIComponent(invoiceId)}/charge?practiceId=${encodeURIComponent(practiceId)}`;
    const token = localStorage.getItem('jwtToken');
    let resp;
    try {
        resp = await fetch(`${PAYMENTS_BASE_URL}${endpoint}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ practiceId, vaultId }),
        });
    } catch (err) {
        console.error('Network error charging invoice with saved card:', err);
        return { ok: false, status: 0, data: { error: 'Network error' } };
    }
    let data = null;
    try { data = await resp.json(); } catch (_) { data = null; }
    return { ok: resp.ok, status: resp.status, data: data || {} };
}
